import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import {
  IconBot, IconLightning, IconSettings, IconShield,
  IconCode, IconArrowRight
} from '../components/Icons';
import './Home.css';

const FEATURES = [
  { Icon: IconCode,      title: 'Developer-Focused',  desc: 'Every bot is purpose-built for developers. Designed around real workflows, not generic use cases.' },
  { Icon: IconBot,       title: 'Proprietary AI',      desc: 'Powered by AI models owned and run by PERM Systems — not OpenAI or any third party. Built specifically for code.' },
  { Icon: IconLightning, title: 'Sub-2s Responses',    desc: 'Optimised infrastructure means commands resolve in under 2 seconds on average, every time.' },
  { Icon: IconSettings,  title: 'Zero Setup',          desc: 'Invite the bot, type a command. No dashboards, no configuration files, no friction.' },
  { Icon: IconShield,    title: 'Private by Design',   desc: 'Minimal permissions. No data selling. Inputs are processed in real time and not retained.' },
  { Icon: IconArrowRight,title: 'Always Improving',    desc: 'One developer, direct feedback loop. Features ship fast and every suggestion is reviewed.' },
];

const STATS = [
  { value: '2026',  label: 'Founded' },
  { value: '10K+',  label: 'Users' },
  { value: '99.9%', label: 'Uptime' },
  { value: '<2s',   label: 'Response time' },
];

const CODE_LINES = [
  { n:1, tokens:[{t:'cm',v:'// Code Assist — powered by PERM AI'}] },
  { n:2, tokens:[] },
  { n:3, tokens:[{t:'kw',v:'const '},{t:'pl',v:'result = '},{t:'kw',v:'await '},{t:'fn',v:'codeAssist'},{t:'pl',v:'.debug({'}] },
  { n:4, tokens:[{t:'str',v:'  language'},{t:'pl',v:': '},{t:'str2',v:'"python"'},{t:'pl',v:','}] },
  { n:5, tokens:[{t:'str',v:'  code'},{t:'pl',v:': snippet,'}] },
  { n:6, tokens:[{t:'pl',v:'});'}] },
  { n:7, tokens:[] },
  { n:8, tokens:[{t:'cm',v:'// [ok] Bug found: ZeroDivisionError on line 3'}] },
  { n:9, tokens:[{t:'cm',v:'// [ok] Fix applied — guard added'}] },
];

function CodeBlock() {
  return (
    <div className="h-editor">
      <div className="h-editor__bar">
        <div className="h-editor__dots">
          <span style={{background:'#ff5f57'}}/>
          <span style={{background:'#febc2e'}}/>
          <span style={{background:'#28c840'}}/>
        </div>
        <span className="h-editor__title">debug.py — Code Assist</span>
        <span className="h-editor__badge">AI Ready</span>
      </div>
      <div className="h-editor__body">
        {CODE_LINES.map(({n, tokens}) => (
          <div key={n} className="h-editor__line">
            <span className="h-editor__num">{n}</span>
            <span className="h-editor__code">
              {tokens.map((tok, j) => (
                <span key={j} className={`tok tok--${tok.t}`}>{tok.v}</span>
              ))}
            </span>
          </div>
        ))}
      </div>
      <div className="h-editor__footer">
        <span className="h-editor__status">
          <span className="h-editor__status-dot"/>
          Connected · PERM AI
        </span>
        <span className="h-editor__lang">Python</span>
      </div>
    </div>
  );
}

function CheckItem({children}) {
  return (
    <li className="h-check">
      <svg className="h-check__icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <circle cx="7" cy="7" r="7" fill="var(--blue-light)"/>
        <polyline points="4 7 6.5 9.5 10 4.5" stroke="var(--blue-dark)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {children}
    </li>
  );
}


/* ── Typewriter hook ── */
const TYPED_PHRASES = [
  'debug your code.',
  'explain any snippet.',
  'generate from scratch.',
  'optimize for speed.',
  'format in seconds.',
  'ship faster.',
];

function useTypewriter(phrases, { typeSpeed = 72, deleteSpeed = 38, pauseAfter = 1600, pauseBefore = 420 } = {}) {
  const [displayed, setDisplayed] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const current = phrases[phraseIdx];

    if (!deleting && charIdx < current.length) {
      timer.current = setTimeout(() => setCharIdx(i => i + 1), typeSpeed);
    } else if (!deleting && charIdx === current.length) {
      timer.current = setTimeout(() => setDeleting(true), pauseAfter);
    } else if (deleting && charIdx > 0) {
      timer.current = setTimeout(() => setCharIdx(i => i - 1), deleteSpeed);
    } else if (deleting && charIdx === 0) {
      timer.current = setTimeout(() => {
        setDeleting(false);
        setPhraseIdx(i => (i + 1) % phrases.length);
      }, pauseBefore);
    }

    setDisplayed(current.slice(0, charIdx));
    return () => clearTimeout(timer.current);
  }, [charIdx, deleting, phraseIdx, phrases, typeSpeed, deleteSpeed, pauseAfter, pauseBefore]);

  return displayed;
}

function HeroHeading() {
  const typed = useTypewriter(TYPED_PHRASES);
  return (
    <>
      <h1 className="h-hero__heading">
        <span className="h-hero__heading-static">Use AI to</span>
        <span className="h-hero__typewriter-line">
          <span className="h-hero__typewriter">{typed}</span>
          <span className="h-hero__cursor" aria-hidden="true"/>
        </span>
      </h1>
      <p className="lead" style={{maxWidth: 480, marginTop: 20}}>
        PERM Systems builds developer tools powered by proprietary AI models. Our first product, Code Assist, helps you debug and understand code directly within Discord. No setup required. Free to use.
      </p>
    </>
  );
}

export default function Home() {
  return (
    <div className="page home-page">

      {/* ── Hero ── */}
      <section className="h-hero">
        <div className="h-hero__bg" aria-hidden="true">
          <div className="h-hero__grid"/>
          <div className="h-hero__glow"/>
        </div>

        <div className="container h-hero__inner">
          <div className="h-hero__content animate-fade-up">
            <div className="h-hero__logo-row">
              <Logo size={44} />
              <span className="h-hero__wordmark">
                <span className="h-hero__wordmark-p">p</span>erm systems
              </span>
            </div>

            <div className="eyebrow">Developer tooling for Discord</div>

            <HeroHeading />

            <div className="h-hero__actions">
              <Link to="/code-assist/home" className="btn btn--primary btn--lg">
                Explore Code Assist
                <IconArrowRight size={15}/>
              </Link>
              <Link to="/about" className="btn btn--outline btn--lg">Our mission</Link>
            </div>

            <div className="h-hero__trust">
              <span className="h-hero__trust-dot"/>
              <span>Free to use · Proprietary AI · No third-party APIs</span>
              <span className="h-hero__trust-divider">·</span>
              <a href="https://thewaterproject.org" target="_blank" rel="noopener noreferrer" className="h-hero__trust-charity">
                Donations support The Water Project
              </a>
            </div>
          </div>

          <div className="h-hero__graphic animate-fade-up delay-3">
            <CodeBlock/>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <div className="h-stats">
        <div className="container">
          <div className="h-stats__grid">
            {STATS.map(({value, label}) => (
              <div key={label} className="h-stats__item">
                <span className="h-stats__value">{value}</span>
                <span className="h-stats__label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Features ── */}
      <section className="section">
        <div className="container">
          <div className="animate-fade-up mob-center" style={{maxWidth: 560}}>
            <div className="eyebrow">Why PERM Systems</div>
            <h2 className="section-title">Built for developers.<br/>Runs on custom AI.</h2>
            <p className="section-body">
              Every decision — from permissions to latency — is made with
              developers in mind. Powered by AI we own and operate.
            </p>
          </div>

          <div className="h-features" style={{marginTop: 56}}>
            {FEATURES.map(({Icon, title, desc}, i) => (
              <div key={title} className={`h-feature animate-fade-up delay-${(i%5)+1}`}>
                <div className="h-feature__icon">
                  <Icon size={19} color="var(--blue-dark)"/>
                </div>
                <div>
                  <h3 className="h-feature__title">{title}</h3>
                  <p className="h-feature__desc">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Code Assist spotlight ── */}
      <section className="section h-spotlight">
        <div className="container h-spotlight__inner">
          <div className="animate-fade-up">
            <div className="eyebrow">Featured Bot</div>
            <h2 className="section-title">Code Assist</h2>
            <div className="rule"/>
            <p className="section-body">
              Launched in 2026, Code Assist is powered entirely by PERM Systems'
              own AI model — tuned for code, not general conversation. No OpenAI.
              No data sharing.
            </p>
            <ul className="h-checklist">
              <CheckItem>Instant AI-powered code debugging</CheckItem>
              <CheckItem>Syntax-highlighted, formatted responses</CheckItem>
              <CheckItem>Supports 20+ programming languages</CheckItem>
              <CheckItem>Native Discord slash command interface</CheckItem>
              <CheckItem>Powered by proprietary PERM AI</CheckItem>
            </ul>
            <div className="h-hero__actions" style={{marginTop: 32}}>
              <Link to="/code-assist/home" className="btn btn--primary btn--lg">
                View Code Assist
              </Link>
              <Link to="/code-assist/invite" className="btn btn--outline btn--lg">
                Invite to server
              </Link>
            </div>
          </div>

          <div className="h-spotlight__card animate-fade-up delay-2">
            <div className="h-spotlight__card-icon">
              <IconCode size={26} color="var(--blue-dark)"/>
            </div>
            <p className="h-spotlight__card-name">Code Assist</p>
            <p className="h-spotlight__card-by">by PERM Systems</p>
            <div className="h-spotlight__card-divider"/>
            <div className="h-spotlight__card-stat">
              <span className="h-spotlight__card-stat-val">2026</span>
              <span className="h-spotlight__card-stat-lbl">Est.</span>
            </div>
            <div className="h-spotlight__card-stat">
              <span className="h-spotlight__card-stat-val">20+</span>
              <span className="h-spotlight__card-stat-lbl">Languages</span>
            </div>
            <div className="h-spotlight__card-divider"/>
            <div className="h-spotlight__card-status">
              <span className="h-spotlight__card-dot"/>
              Operational
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section-sm h-cta">
        <div className="container">
          <div className="h-cta__inner animate-fade-up">
            <div className="h-cta__text">
              <h2 className="section-title" style={{color:'#fff', letterSpacing:'-0.03em'}}>
                Start building better<br/>on Discord.
              </h2>
              <p style={{color:'rgba(255,255,255,0.5)', marginTop:10, fontSize:'0.97rem', lineHeight:1.7}}>
                Free to use · Proprietary AI · No setup required
              </p>
              <p style={{color:'rgba(255,255,255,0.32)', marginTop:6, fontSize:'0.82rem', lineHeight:1.6}}>
                All donations are contributed to{' '}
                <a href="https://thewaterproject.org" target="_blank" rel="noopener noreferrer"
                   style={{color:'#60a5fa', textDecoration:'underline', textUnderlineOffset:'2px'}}>
                  The Water Project
                </a>
              </p>
            </div>
            <div className="h-cta__actions">
              <Link to="/code-assist/invite" className="btn btn--xl"
                style={{background:'var(--white)', color:'var(--black)', fontWeight:700}}>
                Invite Code Assist
              </Link>
              <Link to="/bots" className="btn btn--xl btn--ghost"
                style={{color:'rgba(255,255,255,0.7)', border:'1px solid rgba(255,255,255,0.15)'}}>
                Browse all products
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}